import React, { useState } from 'react';
import { Box, Button, Grid, Typography } from 'components/mui';
import AddIcon from '@mui/icons-material/Add';
import LoadingBackdrop from 'components/LoadingBackdrop';
import { useCaseDashboard } from 'containers/pages/cases/hooks';
import { CaseSearch, CaseList, CaseFilterBar, CaseDetailPanel } from 'containers/pages/cases/components';
import { CaseFormDialog } from 'containers/pages/cases/components/caseForm';
import GPDAReassignDialog from 'containers/pages/cases/components/gpdaReassign/GPDAReassignDialog';
import useGPDAReassign from 'containers/pages/cases/hooks/useGPDAReassign';

function CasesDashboard() {
  const { state, handler } = useCaseDashboard();
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formDialogMode, setFormDialogMode] = useState<'add' | 'edit' | 'copy'>('add');
  const [formCaseId, setFormCaseId] = useState<number | undefined>();

  const reassign = useGPDAReassign();

  const handleEdit = (caseData: Record<string, unknown>) => {
    setFormDialogMode('edit');
    setFormCaseId(caseData.Id as number);
    setSelectedCaseId(null);
    setFormDialogOpen(true);
  };

  const handleDuplicate = (caseData: Record<string, unknown>) => {
    setFormDialogMode('copy');
    setFormCaseId(caseData.Id as number);
    setSelectedCaseId(null);
    setFormDialogOpen(true);
  };

  const handleGPDChanged = (payload: {
    caseId: number;
    caseTitle: string;
    groupId: number;
    groupTitle: string;
    projectId: number;
    projectTitle: string;
    deliverableId: number;
    deliverableTitle: string;
    casePayload: Record<string, unknown>;
  }) => {
    reassign.startReassign(payload.caseId, payload.caseTitle, {
      groupId: payload.groupId,
      groupTitle: payload.groupTitle,
      projectId: payload.projectId,
      projectTitle: payload.projectTitle,
      deliverableId: payload.deliverableId,
      deliverableTitle: payload.deliverableTitle,
    }, payload.casePayload);
  };

  return (
    <Box
      component="main"
      data-tour="cases-page"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: { xs: 2, sm: 3 },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" component="h1">
          Cases
        </Typography>
        <Button
          variant="contained"
          data-tour="new-case-btn"
          startIcon={<AddIcon />}
          onClick={() => {
            setFormDialogMode('add');
            setFormCaseId(undefined);
            setFormDialogOpen(true);
          }}
        >
          New Case
        </Button>
      </Box>

      {/* Control bar: Search + Filter toggle */}
      <Grid container alignItems="stretch" spacing={2} wrap="nowrap" sx={{ mb: 1 }}>
        <Grid item xs>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <CaseSearch
              searchInput={state.searchInput}
              onSearchInput={handler.onSearchInput}
              onReset={handler.onResetSearch}
            />
          </Box>
        </Grid>
      </Grid>

      {/* Filter bar */}
      <CaseFilterBar
        filters={state.filters}
        onFilterChange={handler.onFilterChange}
        onApplyFilters={handler.onApplyFilters}
        onClearFilters={handler.onClearFilters}
      />

      {/* Case list table */}
      <CaseList cases={state.cases} onCaseClick={(id) => setSelectedCaseId(id)} />

      {/* Case detail panel */}
      <CaseDetailPanel
        open={selectedCaseId !== null}
        onClose={() => setSelectedCaseId(null)}
        caseId={selectedCaseId}
        onEdit={handleEdit}
        onDuplicate={handleDuplicate}
        onReferenceClick={(refId) => setSelectedCaseId(refId)}
      />

      {/* Case form dialog */}
      <CaseFormDialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        mode={formDialogMode}
        caseId={formCaseId}
        onSave={() => setFormDialogOpen(false)}
        onGPDChanged={handleGPDChanged}
      />

      {/* GPDA reassignment dialog */}
      <GPDAReassignDialog
        state={reassign.state}
        onUpdateMapping={reassign.updateMapping}
        onApply={reassign.applyMappings}
        onClose={reassign.close}
      />

      <LoadingBackdrop open={state.isLoading} />
    </Box>
  );
}

export default CasesDashboard;
